// src/pages/Admin/DashboardLayout.tsx
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingBag,
  Package,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Truck,
  ShieldCheck,
  Warehouse,
  Menu,
  X,
  User,
  FileCheck,
  Store
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../api/admin';

const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [pendingApplications, setPendingApplications] = useState(0);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      // Close mobile menu on resize to desktop
      if (desktop) {
        setIsMobileMenuOpen(false);
      }
      fetchPendingCount();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen && !isDesktop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, isDesktop]);

  useEffect(() => {
    if (isDesktop) return;

    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      const diffX = touchEndX - touchStartX;
      const diffY = touchEndY - touchStartY;

      // Only if horizontal swipe is more than vertical
      if (Math.abs(diffX) > Math.abs(diffY) * 1.5) {
        if (diffX > 50 && !isMobileMenuOpen) {
          // Swipe right to open
          setIsMobileMenuOpen(true);
        } else if (diffX < -50 && isMobileMenuOpen) {
          // Swipe left to close
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDesktop, isMobileMenuOpen]);

  const fetchPendingCount = async () => {
    try {
      const response = await adminAPI.getVendorApplications('pending');
      setPendingApplications(response.data?.length || 0);
    } catch (error) {
      console.error('Failed to fetch pending count:', error);
    }
  };

  const navigation = [
    { name: 'Overview', to: '/dashboard', icon: LayoutDashboard, exact: true },
    { name: 'Orders', to: '/dashboard/orders', icon: ShoppingBag },
    { name: 'Products', to: '/dashboard/products', icon: Package },
    { name: 'Users', to: '/dashboard/users', icon: Users },
    { name: 'Verifications', to: '/dashboard/verifications', icon: ShieldCheck },
    { name: 'Warehouse', to: '/dashboard/warehouse', icon: Warehouse },
    { name: 'Drivers', to: '/dashboard/drivers', icon: Truck },
    // { name: 'Vendor Applications', to: '/dashboard/vendor-applications', icon: FileCheck },
    { name: 'Vendor Management', to: '/dashboard/vendors', icon: Store }, 
    { name: 'Settings', to: '/dashboard/settings', icon: Settings },
  ];

  // Get page title for mobile header
  const getPageTitle = () => {
    const path = location.pathname;
    const currentNav = navigation.find(item =>
      item.exact ? path === item.to : path.startsWith(item.to)
    );
    return currentNav?.name || 'Dashboard';
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  // Sidebar content (shared between desktop and mobile)
  const SidebarContent = () => (
    <>
      {/* Logo area */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 flex-shrink-0">
        {(!isCollapsed || !isDesktop) && (
          <span className="text-xl font-bold text-gray-900">
            {isDesktop ? 'Admin Panel' : 'Trincity Admin'}
          </span>
        )}
        {isDesktop && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        )}
        {!isDesktop && (
          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.to}
            end={item.exact}
            className={({ isActive }) =>
              `flex items-center px-4 py-3 text-sm font-medium transition-colors ${isActive
                ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                : 'text-gray-700 hover:bg-gray-50'
              } ${isCollapsed ? 'justify-center' : 'space-x-3'}`
            }
          >
            <item.icon size={20} />
            {!isCollapsed && (
              <>
                <span>{item.name}</span>
                {item.name === 'Vendor Applications' && pendingApplications > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {pendingApplications}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="border-t border-gray-200 p-4 flex-shrink-0">
        <div className={`flex items-center ${isCollapsed && isDesktop ? 'justify-center' : 'space-x-3'}`}>
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-blue-600">
              {user?.first_name?.[0]}{user?.last_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
            </span>
          </div>
          {(!isCollapsed || !isDesktop) && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 truncate capitalize">
                {user?.primaryRole || user?.role || 'User'}
              </p>
            </div>
          )}
        </div>
        <button
          onClick={logout}
          className={`mt-4 w-full flex items-center text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg px-3 py-2 transition-colors ${isCollapsed && isDesktop ? 'justify-center' : 'space-x-2'
            }`}
        >
          <LogOut size={18} />
          {(!isCollapsed || !isDesktop) && <span>Logout</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar - always visible on lg+ */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 ${isCollapsed ? 'w-20' : 'w-64'
          }`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {!isDesktop && isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity lg:hidden"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Mobile Sidebar - Slide in */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-white shadow-2xl z-50 transition-transform duration-300 ease-out lg:hidden
          w-[85vw] max-w-[320px]
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile/Tablet Header */}
        <header className="lg:hidden flex items-center justify-between h-14 px-4 bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleMobileMenu}
              className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {getPageTitle()}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard/profile')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Profile"
            >
              <User className="w-5 h-5" />
            </button>
            <button
              onClick={logout}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              aria-label="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="py-4 px-4 sm:py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;